/**
 * Active Subscription Hook
 * 
 * React Query hook for the member's current-subscription read: the elected plan
 * (team membership, else personal membership, else pass — the library's
 * `PLAN_ELECTION`) and, under `holdings`, every row they hold.
 * 
 * Returns the full React Query result with derived convenience properties.
 * This follows the "extended return" pattern (see useClubQuery for reference):
 * - Spread the full useQuery result (preserves isFetching, dataUpdatedAt, etc.)
 * - Add derived convenience fields (activeSubscription, hasActiveSubscription, etc.)
 * 
 * @module subscription/hooks
 */

import { useQuery } from '@tanstack/react-query';
import { queryKeys } from '~/lib/query/keys';
import { cachePresets } from '~/lib/query/cachePresets';
import { useClubId } from '~/services/appRuntime';
import { subscriptionService } from '../api/subscriptionService';
import type { UseActiveSubscriptionReturn } from '../types';

export interface UseActiveSubscriptionOptions {
  /** User ID to fetch subscription for */
  userId: number | null | undefined;
  
  /** Whether the query is enabled (default: true if userId exists) */
  enabled?: boolean;
}

/**
 * Fetch the member's elected plan and holdings
 * 
 * Returns the full React Query result plus derived convenience fields.
 * Components can destructure just what they need:
 * 
 * @example
 * ```tsx
 * // Simple usage - derived fields only
 * const { activeSubscription, hasActiveSubscription } = useActiveSubscription({ userId });
 * 
 * // Advanced usage - access full React Query state
 * const { isFetching, dataUpdatedAt, activeSubscription } = useActiveSubscription({ userId });
 * ```
 */
export function useActiveSubscription(options: UseActiveSubscriptionOptions): UseActiveSubscriptionReturn {
  const { userId, enabled = true } = options;
  const [clubId] = useClubId();
  const clubIdNumber = clubId ? Number(clubId) : null;
  
  const query = useQuery({
    // Use subscriptions.active key (not memberships.active)
    // This query returns membership OR pass, so it belongs in the subscriptions namespace
    queryKey: clubIdNumber && userId 
      ? queryKeys.subscriptions.active(clubIdNumber, userId)
      : queryKeys.disabled.subscriptions(),
    queryFn: async () => {
      if (!userId) {
        throw new Error('User ID is required');
      }
      
      const result = await subscriptionService.getActiveSubscription(userId);
      
      if (!result.success) {
        throw new Error(result.message || 'Failed to fetch subscription');
      }
      
      return result.payload;
    },
    enabled: enabled && !!userId && !!clubIdNumber,
    // Subscriptions can change (user buys membership), so use stable cache
    ...cachePresets.stable,
  });

  // Derived convenience properties
  const activeSubscription = query.data?.subscription ?? null;
  const hasActiveSubscription = !!activeSubscription;
  const hasActiveMembership = query.data?.hasActiveMembership ?? false;
  const hasActivePass = query.data?.hasActivePass ?? false;
  const demoCompleted = query.data?.demoCompleted ?? false;
  const subscriptionName = activeSubscription?.plan?.name;
  const holdings = query.data?.holdings ?? null;

  return {
    ...query,
    // Derived convenience fields
    activeSubscription,
    hasActiveSubscription,
    hasActiveMembership,
    hasActivePass,
    demoCompleted,
    subscriptionName,
    holdings,
  };
}
